/**
 * iOS-specific authentication using @capacitor-community/generic-oauth2
 * This uses ASWebAuthenticationSession to comply with Apple App Review requirements
 */
import { Capacitor } from '@capacitor/core';
import { GenericOAuth2 } from '@capacitor-community/generic-oauth2';
import { saveTokens, clearTokens, getTokens, type TokenData } from './tokenStorage';

// Re-export for convenience
export type { TokenData };

// Type definitions for OAuth2Client
interface OAuth2AuthenticateOptions {
  appId: string;
  authorizationBaseUrl: string;
  accessTokenEndpoint: string;
  responseType: string;
  pkceEnabled: boolean;
  scope: string;
  logsEnabled?: boolean;
  ios?: { redirectUrl: string };
  android?: { redirectUrl: string };
  web?: { redirectUrl: string; windowOptions?: string };
  additionalParameters?: Record<string, string>;
  refreshToken?: string;
}

const REDIRECT_URL = 'bestihealth.oauth://callback';

function getOAuthConfig(): OAuth2AuthenticateOptions {
  const domain = import.meta.env.VITE_COGNITO_DOMAIN;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  
  if (!domain || !clientId) {
    throw new Error('Missing required Cognito environment variables');
  }

  return {
    appId: clientId,
    authorizationBaseUrl: `${domain}/oauth2/authorize`,
    accessTokenEndpoint: `${domain}/oauth2/token`,
    responseType: 'code',
    pkceEnabled: true,
    scope: 'openid email phone profile',
    logsEnabled: true,
    ios: {
      redirectUrl: REDIRECT_URL,
    },
    android: {
      redirectUrl: REDIRECT_URL,
    },
    web: {
      redirectUrl: `${window.location.origin}/callback`,
      windowOptions: 'height=600,left=0,top=0',
    },
    additionalParameters: {
      redirect_uri: REDIRECT_URL
    }
  };
}


/**
 * Sign in using Cognito Hosted UI via ASWebAuthenticationSession on iOS
 * This opens the system auth sheet (not Safari) which is Apple-compliant
 */
export async function mobileSignIn(): Promise<TokenData> {
  try {
    console.log('[iOS Auth] ========== STARTING OAUTH FLOW ==========');
    const config = getOAuthConfig();
    
    // Log full config
    console.log('[iOS Auth] Full OAuth Config:');
    console.log(JSON.stringify(config, null, 2));
    
    // Manually construct what the authorization URL SHOULD be
    const expectedAuthUrl = `${config.authorizationBaseUrl}?client_id=${config.appId}&redirect_uri=${encodeURIComponent(REDIRECT_URL)}&response_type=code&scope=${encodeURIComponent(config.scope)}`;
    console.log('[iOS Auth] Expected authorization URL (if no PKCE):');
    console.log(expectedAuthUrl);
    
    console.log('[iOS Auth] Calling GenericOAuth2.authenticate...');
    console.log('[iOS Auth] This will construct the actual authorization URL and open ASWebAuthenticationSession');
    
    const result = await GenericOAuth2.authenticate(config);
    console.log('[iOS Auth] Authentication successful!');
    console.log('[iOS Auth] Result:', JSON.stringify(result, null, 2));
    
    // Extract tokens
    const tokenData: TokenData = {
      access_token: result.access_token,
      id_token: result.id_token || '',
      expires_at: result.access_token_expiration_date 
        ? new Date(result.access_token_expiration_date).getTime()
        : Date.now() + (3600 * 1000),
    };

    // Save to secure storage
    await saveTokens(tokenData);
    console.log('[iOS Auth] Tokens saved to storage');
    
    return tokenData;
  } catch (error) {
    console.error('[iOS Auth] Sign-in error:', error);
    console.error('[iOS Auth] Error details:', JSON.stringify(error, null, 2));
    throw error;
  }
}

/**
 * Refresh the access token (Cognito doesn't support refresh via GenericOAuth2, requires re-login)
 */
export async function mobileRefreshToken(): Promise<TokenData> {
  console.log('[iOS Auth] Token refresh not supported via GenericOAuth2, user must sign in again');
  throw new Error('Token refresh requires re-authentication');
}

/**
 * Sign out
 */
export async function mobileSignOut(): Promise<void> {
  try {
    console.log('[iOS Auth] Signing out...');
    await clearTokens();
    console.log('[iOS Auth] Tokens cleared');
  } catch (error) {
    console.error('[iOS Auth] Sign-out error:', error);
    await clearTokens();
    throw error;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    const tokens = await getTokens();
    
    if (!tokens?.access_token) {
      return false;
    }

    // Check if token is expired
    const now = Date.now();
    if (tokens.expires_at && tokens.expires_at < now) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Get current access token
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const tokens = await getTokens();
    return tokens?.access_token || null;
  } catch (error) {
    return null;
  }
}

/**
 * Get current ID token
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const tokens = await getTokens();
    return tokens?.id_token || null;
  } catch (error) {
    return null;
  }
}

/**
 * Check if running on iOS
 */
export function isIOS(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

